import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { DraggableFieldList } from '../../../components/Common/DraggableFieldList';
import {
    brandContestDialogFooterSx,
    brandContestDialogPaperSx,
    editorialHeadlineSx,
    editorialInputSx,
    editorialLabelSx,
    midnight,
    selectionCardSx,
} from './midnightStyles';
import type { ContestQuickCreateDraft } from './contestAutofillHelpers';
import {
    getQuickCreateEditableAwardRows,
    getQuickCreateEditableTierRows,
    isContestQuickCreateValid,
    isQuickCreateCategoryAwardsConfigured,
    isQuickCreateCategorySetupStarted,
    removeQuickCreateAwardPayout,
    removeQuickCreateTier,
    updateQuickCreateAwardPrize,
    updateQuickCreateTierFee,
    updateQuickCreateTierLabel,
    SUBMISSION_TYPE_OPTIONS,
} from './contestAutofillHelpers';
import { ContestQuickCategoryAwardsDialog } from './ContestQuickCategoryAwardsDialog';
import { ContestTiersAwardsHelpButton } from './ContestTiersAwardsHelpButton';
import { emptyWizardCategory } from '../contestManage/contestCategoryWizardUtils';
import { RichTextEditor } from '../../../components/Common/RichTextEditor';

export interface ContestQuickCreateDialogProps {
    open: boolean;
    initialDraft: ContestQuickCreateDraft;
    submitting?: boolean;
    onClose: () => void;
    onCreate: (draft: ContestQuickCreateDraft) => void;
}

export function ContestQuickCreateDialog({
    open,
    initialDraft,
    submitting,
    onClose,
    onCreate,
}: ContestQuickCreateDialogProps) {
    const [draft, setDraft] = useState<ContestQuickCreateDraft>(initialDraft);
    const [categoryAwardsOpen, setCategoryAwardsOpen] = useState(false);

    useEffect(() => {
        if (open) setDraft(initialDraft);
    }, [open, initialDraft]);

    const canCreate = useMemo(() => isContestQuickCreateValid(draft), [draft]);
    const category = draft.categories[0] ?? emptyWizardCategory();
    const categorySetupStarted = useMemo(() => isQuickCreateCategorySetupStarted(draft), [draft]);
    const categoryAwardsConfigured = useMemo(() => isQuickCreateCategoryAwardsConfigured(draft), [draft]);
    const editableTierRows = useMemo(
        () => (categorySetupStarted ? getQuickCreateEditableTierRows(category) : []),
        [categorySetupStarted, category],
    );
    const editableAwardRows = useMemo(
        () => (categorySetupStarted ? getQuickCreateEditableAwardRows(category) : []),
        [categorySetupStarted, category],
    );

    const quickCreateMoneyInputSx = {
        '& .MuiOutlinedInput-root': {
            bgcolor: midnight.surfaceHighest,
            borderRadius: '10px',
            fontSize: 13,
            fontWeight: 700,
            color: midnight.primary,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(255,255,255,0.65)',
                borderWidth: 1,
            },
        },
        '& .MuiOutlinedInput-input': {
            py: 0.75,
            px: 0.5,
            textAlign: 'right' as const,
        },
    };

    const quickCreateTextInputSx = {
        flex: 1,
        minWidth: 0,
        '& .MuiOutlinedInput-root': {
            bgcolor: midnight.surfaceHighest,
            borderRadius: '10px',
            fontSize: 13,
            fontWeight: 600,
            color: midnight.onSurface,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
            '&.Mui-focused fieldset': {
                borderColor: 'rgba(255,255,255,0.65)',
                borderWidth: 1,
            },
        },
        '& .MuiOutlinedInput-input': {
            py: 0.75,
            px: 1,
        },
    };

    const parseAwardPrize = (raw: string): number => {
        const n = parseFloat(raw.replace(/[^\d.]/g, ''));
        return Number.isNaN(n) ? 0 : n;
    };

    const handleAwardPrizeChange = (placementIndex: number, payoutIndex: number, raw: string) => {
        if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
        const cats = updateQuickCreateAwardPrize(
            draft.categories.length ? draft.categories : [emptyWizardCategory()],
            placementIndex,
            payoutIndex,
            raw === '' ? 0 : parseAwardPrize(raw),
        );
        patch({ categories: cats });
    };

    const handleRemoveAward = (placementIndex: number, payoutIndex: number) => {
        const cats = removeQuickCreateAwardPayout(
            draft.categories.length ? draft.categories : [emptyWizardCategory()],
            placementIndex,
            payoutIndex,
        );
        patch({ categories: cats });
    };

    const handleTierLabelChange = (tierIndex: number, label: string) => {
        const cats = updateQuickCreateTierLabel(
            draft.categories.length ? draft.categories : [emptyWizardCategory()],
            tierIndex,
            label,
        );
        patch({ categories: cats });
    };

    const handleTierFeeChange = (tierIndex: number, raw: string) => {
        if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
        const cats = updateQuickCreateTierFee(
            draft.categories.length ? draft.categories : [emptyWizardCategory()],
            tierIndex,
            raw === '' ? 0 : parseAwardPrize(raw),
        );
        patch({ categories: cats });
    };

    const handleRemoveTier = (tierIndex: number) => {
        const cats = removeQuickCreateTier(
            draft.categories.length ? draft.categories : [emptyWizardCategory()],
            tierIndex,
        );
        patch({ categories: cats });
    };

    const patch = (partial: Partial<ContestQuickCreateDraft>) => setDraft(prev => ({ ...prev, ...partial }));

    const setCategoryField = (field: 'category_name' | 'category_description', value: string) => {
        const cats = [...(draft.categories.length ? draft.categories : [emptyWizardCategory()])];
        cats[0] = { ...cats[0], [field]: value };
        patch({ categories: cats });
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            PaperProps={{ sx: brandContestDialogPaperSx }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 3, pt: 2.5 }}>
                <Box sx={{ pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(255,201,101,0.12)',
                                border: '1px solid rgba(255,201,101,0.35)',
                                color: midnight.primary,
                            }}>
                            <BoltIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>
                            Quick Contest Setup
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            color: midnight.onSurfaceVariant,
                            fontSize: 14,
                            lineHeight: 1.55,
                            maxWidth: 520,
                            mt: 1.5,
                        }}>
                        Required fields are pre-filled so you can preview, edit, and create your campaign in one step.
                        Optional settings can be completed later in the campaign setup.
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    disabled={submitting}
                    aria-label="Close"
                    sx={{ color: midnight.onSurfaceVariant, mt: -0.5 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    overflow: 'auto',
                    maxHeight: { xs: 'calc(100vh - 220px)', sm: 'calc(92vh - 220px)' },
                }}>
                <Box>
                    <Typography sx={editorialLabelSx}>Submission type *</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
                        {SUBMISSION_TYPE_OPTIONS.map(opt => {
                            const selected = draft.submission_state === opt.value;
                            const Icon = opt.value === 'public_submission' ? PublicIcon : LockIcon;
                            return (
                                <Box
                                    key={opt.value}
                                    component="button"
                                    type="button"
                                    onClick={() =>
                                        patch({
                                            submission_state: opt.value,
                                            submission_state_message: opt.message,
                                        })
                                    }
                                    sx={{
                                        ...selectionCardSx(selected),
                                        width: '100%',
                                        border: 'none',
                                        cursor: 'pointer',
                                        p: 2.5,
                                        textAlign: 'left',
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Icon
                                            sx={{
                                                fontSize: 20,
                                                color: selected ? midnight.primary : midnight.onSurfaceVariant,
                                            }}
                                        />
                                        <Typography sx={{ fontWeight: 700, color: midnight.onSurface, fontSize: 14 }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{ fontSize: 12, color: midnight.onSurfaceVariant, lineHeight: 1.45 }}>
                                        {opt.value === 'public_submission'
                                            ? 'Visible on Explore — open to qualified creators.'
                                            : 'Invite-only — hidden from the public feed.'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box>
                    <Typography sx={editorialLabelSx}>Campaign title *</Typography>
                    <TextField
                        fullWidth
                        placeholder="e.g. Midnight Summer Film Challenge"
                        value={draft.title}
                        onChange={e => patch({ title: e.target.value })}
                        sx={editorialInputSx}
                    />
                </Box>

                <Box>
                    <Typography sx={editorialLabelSx}>Campaign brief *</Typography>
                    <RichTextEditor
                        shellVariant="brandDashboard"
                        placeholder="Describe goals, tone, and what success looks like."
                        value={draft.description}
                        onChange={description => patch({ description })}
                    />
                </Box>

                <Box>
                    <Typography sx={editorialLabelSx}>Requirements *</Typography>
                    <Typography sx={{ fontSize: 12, color: midnight.onSurfaceVariant, mb: 1 }}>
                        At least one requirement. Drag to reorder.
                    </Typography>
                    <DraggableFieldList
                        items={draft.requirements}
                        onItemsChange={items => patch({ requirements: items })}
                        placeholder="Add a requirement…"
                        inputSx={editorialInputSx}
                    />
                </Box>

                <Box
                    sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        bgcolor: midnight.surfaceLow,
                        border: '1px solid rgba(255,255,255,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={draft.categories_enabled}
                                onChange={e => {
                                    const enabled = e.target.checked;
                                    patch({
                                        categories_enabled: enabled,
                                        categories: enabled
                                            ? draft.categories.length
                                                ? draft.categories
                                                : [emptyWizardCategory()]
                                            : [],
                                    });
                                }}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: midnight.primary },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        bgcolor: midnight.primary,
                                    },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontWeight: 600, color: midnight.onSurface, fontSize: 14 }}>
                                Enable submission categories
                            </Typography>
                        }
                    />

                    {draft.categories_enabled && (
                        <>
                            {!categorySetupStarted ? (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: '12px',
                                        border: '1px dashed rgba(255,201,101,0.35)',
                                        bgcolor: 'rgba(255,201,101,0.04)',
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
                                        <Typography sx={{ fontSize: 13, color: midnight.onSurfaceVariant, flex: 1 }}>
                                            Set up your submission category, tiers, and cash prizes.
                                        </Typography>
                                        <ContestTiersAwardsHelpButton />
                                    </Box>
                                    <Button
                                        onClick={() => setCategoryAwardsOpen(true)}
                                        sx={{
                                            borderRadius: 999,
                                            fontWeight: 800,
                                            fontSize: 11,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: '#392700',
                                            background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                                            px: 3,
                                            py: 1,
                                        }}>
                                        Set up category & awards
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <Box>
                                        <Typography sx={editorialLabelSx}>Category name *</Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="e.g. Main Competition"
                                            value={category.category_name}
                                            onChange={e => setCategoryField('category_name', e.target.value)}
                                            sx={editorialInputSx}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={editorialLabelSx}>Category description *</Typography>
                                        <RichTextEditor
                                            shellVariant="brandDashboard"
                                            compact
                                            placeholder="Who should enter this category and what makes it distinct?"
                                            value={category.category_description || ''}
                                            onChange={category_description =>
                                                setCategoryField('category_description', category_description)
                                            }
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: '12px',
                                            bgcolor: midnight.surfaceContainer,
                                            border: '1px solid rgba(255,255,255,0.06)',
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 1.5,
                                            }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                <Typography sx={{ ...editorialLabelSx, mb: 0 }}>
                                                    Tiers & awards
                                                </Typography>
                                                <ContestTiersAwardsHelpButton />
                                            </Box>
                                            <Button
                                                size="small"
                                                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => setCategoryAwardsOpen(true)}
                                                sx={{
                                                    color: midnight.primary,
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.06em',
                                                }}>
                                                Edit
                                            </Button>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.12em',
                                                    textTransform: 'uppercase',
                                                    color: midnight.onSurfaceVariant,
                                                    mb: 1,
                                                }}>
                                                Tiers
                                            </Typography>
                                            {editableTierRows.length === 0 ? (
                                                <Typography
                                                    sx={{ fontSize: 13, color: midnight.onSurfaceVariant, py: 0.5 }}>
                                                    No tiers yet — use Edit to add entry tiers.
                                                </Typography>
                                            ) : (
                                                editableTierRows.map((row, i) => (
                                                    <Box
                                                        key={`tier-${row.tierIndex}`}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            py: 1,
                                                            borderTop:
                                                                i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                                                        }}>
                                                        <WorkspacePremiumOutlinedIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: midnight.primary,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            value={row.label}
                                                            onChange={e =>
                                                                handleTierLabelChange(row.tierIndex, e.target.value)
                                                            }
                                                            placeholder="Tier name"
                                                            inputProps={{
                                                                'aria-label': `Tier ${i + 1} name`,
                                                            }}
                                                            sx={quickCreateTextInputSx}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            value={row.fee ? String(row.fee) : ''}
                                                            onChange={e =>
                                                                handleTierFeeChange(row.tierIndex, e.target.value)
                                                            }
                                                            placeholder="0"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 13,
                                                                                fontWeight: 700,
                                                                                color: midnight.primary,
                                                                            }}>
                                                                            $
                                                                        </Typography>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            inputProps={{
                                                                inputMode: 'decimal',
                                                                'aria-label': `Entry fee for ${row.label || 'tier'}`,
                                                            }}
                                                            sx={{
                                                                width: 108,
                                                                flexShrink: 0,
                                                                ...quickCreateMoneyInputSx,
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Remove tier ${row.label || i + 1}`}
                                                            onClick={() => handleRemoveTier(row.tierIndex)}
                                                            sx={{
                                                                color: midnight.onSurfaceVariant,
                                                                flexShrink: 0,
                                                                '&:hover': { color: '#f87171' },
                                                            }}>
                                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.12em',
                                                    textTransform: 'uppercase',
                                                    color: midnight.onSurfaceVariant,
                                                    mb: 1,
                                                }}>
                                                Awards
                                            </Typography>
                                            {editableAwardRows.length === 0 ? (
                                                <Typography
                                                    sx={{ fontSize: 13, color: midnight.onSurfaceVariant, py: 1 }}>
                                                    No awards yet — use Edit to add prizes.
                                                </Typography>
                                            ) : (
                                                editableAwardRows.map((row, i) => (
                                                    <Box
                                                        key={`${row.placementIndex}-${row.payoutIndex}`}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            py: 1,
                                                            borderTop:
                                                                i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                                                        }}>
                                                        <EmojiEventsOutlinedIcon
                                                            sx={{
                                                                fontSize: 16,
                                                                color: midnight.primary,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontSize: 14,
                                                                color: midnight.onSurface,
                                                                flex: 1,
                                                                minWidth: 0,
                                                            }}>
                                                            {row.title}
                                                        </Typography>
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            value={row.prize ? String(row.prize) : ''}
                                                            onChange={e =>
                                                                handleAwardPrizeChange(
                                                                    row.placementIndex,
                                                                    row.payoutIndex,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            placeholder="0"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 13,
                                                                                fontWeight: 700,
                                                                                color: midnight.primary,
                                                                            }}>
                                                                            $
                                                                        </Typography>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            inputProps={{
                                                                inputMode: 'decimal',
                                                                'aria-label': `Prize amount for ${row.title}`,
                                                            }}
                                                            sx={{
                                                                width: 108,
                                                                flexShrink: 0,
                                                                ...quickCreateMoneyInputSx,
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Remove ${row.title}`}
                                                            onClick={() =>
                                                                handleRemoveAward(row.placementIndex, row.payoutIndex)
                                                            }
                                                            sx={{
                                                                color: midnight.onSurfaceVariant,
                                                                flexShrink: 0,
                                                                '&:hover': { color: '#f87171' },
                                                            }}>
                                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Box>
                                                ))
                                            )}
                                            {!categoryAwardsConfigured && editableAwardRows.length > 0 && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: midnight.primary,
                                                        mt: 1,
                                                    }}>
                                                    Add a prize amount above $0 for each award, or use Edit to add more.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            <ContestQuickCategoryAwardsDialog
                open={categoryAwardsOpen}
                categories={draft.categories.length ? draft.categories : [emptyWizardCategory()]}
                onClose={() => setCategoryAwardsOpen(false)}
                onComplete={categories => {
                    patch({ categories, categories_enabled: true });
                    setCategoryAwardsOpen(false);
                }}
            />

            <Box
                sx={{
                    ...brandContestDialogFooterSx,
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        onClick={onClose}
                        disabled={submitting}
                        sx={{
                            px: 3,
                            py: 1.25,
                            borderRadius: 999,
                            color: midnight.onSurfaceVariant,
                            fontWeight: 700,
                            fontSize: 12,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                        }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onCreate(draft)}
                        disabled={!canCreate || submitting}
                        sx={{
                            px: 4,
                            py: 1.25,
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: 12,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            color: '#392700',
                            background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                            boxShadow: '0 10px 30px rgba(255,201,101,0.2)',
                            minWidth: 160,
                            '&:disabled': { opacity: 0.45 },
                        }}>
                        {submitting ? <CircularProgress size={18} sx={{ color: '#392700' }} /> : 'Create contest'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
